import React, { useEffect, useState, useRef } from 'react'
import { 
  Card, Button, Form, Input, Typography, Space, 
  Tag, Divider, Checkbox, App, Badge, Segmented, Upload, Select, Image, Modal 
} from 'antd'
import { 
  PictureOutlined, 
  VideoCameraOutlined, 
  DownloadOutlined,
  HistoryOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined,
  HeartOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  BulbOutlined,
  FireOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ImageGenerationType, AspectRatio } from '@/types'
import { on as onEvent } from '@/services/events'
import { apiClient } from '@/services/api'
import { useAuthStore } from '@/store/authStore'
import { GlobalStyles, COMMON_STYLES } from '@/styles/globalStyles'
import '@/styles/global.css'
import { textService } from '@/services/textService'

const { Title, Paragraph, Text } = Typography
const { Dragger } = Upload
const { Option } = Select
const { TextArea } = Input

const ImageGenerationPage: React.FC = () => {
  const { message } = App.useApp()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('PORTRAIT_9_16')
  const [count, setCount] = useState(1)
  const [removeWatermark, setRemoveWatermark] = useState(false)
  const [generatedImages, setGeneratedImages] = useState<any[]>([])
  const [refImageFile, setRefImageFile] = useState<File | null>(null)
  const [refImageUrl, setRefImageUrl] = useState<string | null>(null)
  const [uploadingRef, setUploadingRef] = useState(false)
  const [previewVisible, setPreviewVisible] = useState(false)
  const [inlineError, setInlineError] = useState<string | null>(null)
  const [promptValue, setPromptValue] = useState('')
  const promptInputRef = useRef<any>(null)
  const [mobileTab, setMobileTab] = useState<'create' | 'result'>('create')
  const navigate = useNavigate()
  const [inspirations, setInspirations] = useState<any[]>([])
  const [loadingInspirations, setLoadingInspirations] = useState(false)
  
  // 添加图片 API 提供商和模型状态
  const [imageProviders, setImageProviders] = useState<any[]>([])
  const [loadingProviders, setLoadingProviders] = useState(false)
  const [imageModels, setImageModels] = useState<any[]>([])
  const [loadingModels, setLoadingModels] = useState(false)
  const [selectedModel, setSelectedModel] = useState<string>('')
  const [polishing, setPolishing] = useState(false)

  // 防重复点击保护
  const lastClickTimeRef = useRef(0)
  const MIN_CLICK_INTERVAL = 2000 // 2秒最小间隔

  // 从localStorage加载用户设置
  useEffect(() => {
    const savedSettings = localStorage.getItem('imageGenerationSettings')
    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings)
        if (settings.aspectRatio) setAspectRatio(settings.aspectRatio)
        if (settings.count) setCount(settings.count)
        if (settings.removeWatermark !== undefined) setRemoveWatermark(settings.removeWatermark)
      } catch (error) {
        console.error('Failed to load saved settings:', error)
      }
    }
  }, [])

  // 保存用户设置到localStorage
  const saveSettings = () => {
    const settings = {
      aspectRatio,
      count,
      removeWatermark
    }
    localStorage.setItem('imageGenerationSettings', JSON.stringify(settings))
  }

  // 当设置改变时保存
  useEffect(() => {
    saveSettings()
  }, [aspectRatio, count, removeWatermark])

  // 加载图片 API 提供商
  const loadProviders = async () => {
    try {
      setLoadingProviders(true)
      // 🔥 修复：使用公开API而不是管理员API
      const response = await apiClient.get('/image/models')
      
      if (response.success && response.data) {
        const { providers, models } = response.data
        
        // 规范化 priority 为数字
        const normalized = models.map((m: any) => ({
          ...m,
          priority: Number(m.priority ?? 999)
        }))
        
        // 将模型按提供商分组，并按优先级排序
        const sortedModels = normalized.sort((a: any, b: any) => {
          // 按优先级从高到低排序（1 为最高，其次 2、3...）
          const priorityA = Number(a.priority ?? 999)
          const priorityB = Number(b.priority ?? 999)
          return priorityA - priorityB
        })

        // 调试输出排序结果
        console.log('📑 [图片页面] 排序后的模型顺序:', sortedModels.map((m:any)=>`${m.providerName}:${m.name}#${m.priority}`).join(' | '))
        
        const providersWithModels = providers.map((provider: any) => ({
          ...provider,
          models: sortedModels.filter((model: any) => model.provider === provider.provider)
        }))
        
        setImageProviders(providersWithModels)
        setImageModels(sortedModels)
        console.log('📊 [图片页面] 已加载提供商:', providersWithModels.length)
      console.log('📊 [图片页面] 已加载模型:', models.length)
        console.log('📊 [图片页面] 提供商详情:', providersWithModels)
      
        // 设置默认模型
        if (sortedModels.length > 0) {
          const currentExists = selectedModel && sortedModels.some((m:any) => m.id === selectedModel)
          if (!currentExists) {
            const top = sortedModels[0] // 最高优先级（1 最小）
            console.log(`🔧 [图片页面] 设置默认模型: ${top.id} (${top.name} · ${top.providerName})`)
            setSelectedModel(top.id)
        console.log('✅ [图片页面] 默认模型已设置')
          }
      } else if (models.length === 0) {
        console.warn('⚠️ [图片页面] 没有可用模型')
      } else {
        console.log('📌 [图片页面] 保持当前选中的模型:', selectedModel)
        }
      } else {
        console.warn('⚠️ [图片页面] 获取提供商失败:', response.message)
        setImageProviders([])
      }
    } catch (error) {
      console.error('❌ [图片页面] 加载提供商失败:', error)
      setImageProviders([])
    } finally {
      setLoadingProviders(false)
    }
  }


  // 初始加载提供商
  useEffect(() => {
    loadProviders()
  }, [])

  // 加载热门创作灵感
  useEffect(() => {
    loadInspirations()
    
    // 每30秒自动刷新灵感
    const refreshInterval = setInterval(() => {
      console.log('🔄 [图片页面] 自动刷新灵感')
      loadInspirations()
    }, 30000)
    
    return () => clearInterval(refreshInterval)
  }, [])

  // 当页面重新可见时刷新灵感
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('👀 [图片页面] 页面重新可见，刷新灵感')
        loadInspirations()
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  const loadInspirations = async () => {
    try {
      setLoadingInspirations(true)
      const response = await apiClient.get('/inspiration/public', {
        params: {
          type: 'IMAGE',
          limit: 6
        }
      })
      
      // apiClient 已经处理过响应，response 就是 {success, data} 格式
      if (response.success && response.data && Array.isArray(response.data) && response.data.length > 0) {
        const items = response.data.map((item: any) => ({
          ...item,
          tags: typeof item.tags === 'string' ? JSON.parse(item.tags) : item.tags
        }))
        setInspirations(items)
        console.log('✅ [图片页面] 灵感加载成功:', items.length, '个')
      } else {
        // 数据为空时使用默认示例
        console.warn('⚠️ [图片页面] 后端无灵感数据，使用默认示例')
        setInspirations([
          { id: '1', emoji: '📸', title: '写实摄影', prompt: '一位优雅的女性，专业摄影，柔和自然光，浅色背景，高清细节，时尚杂志风格' },
          { id: '2', emoji: '🎌', title: '动漫风格', prompt: '可爱的动漫少女，大眼睛，粉色头发，校园制服，樱花背景，日本动漫画风' },
          { id: '3', emoji: '🎨', title: '水彩艺术', prompt: '春天的田野，野花盛开，蝴蝶飞舞，水彩画风格，柔和色调，艺术感' },
          { id: '4', emoji: '🌃', title: '赛博朋克', prompt: '未来城市街道，霓虹灯招牌，雨夜反光，赛博朋克风格，高对比度，科幻感' },
          { id: '5', emoji: '🏔️', title: '自然风光', prompt: '雪山湖泊，倒影清晰，蓝天白云，4K高清，自然纪录片风格' }
        ])
      }
    } catch (error) {
      console.error('❌ [图片页面] 加载灵感失败:', error)
      // 失败时也使用默认示例
      setInspirations([
        { id: '1', emoji: '📸', title: '写实摄影', prompt: '一位优雅的女性，专业摄影，柔和自然光，浅色背景，高清细节，时尚杂志风格' },
        { id: '2', emoji: '🎌', title: '动漫风格', prompt: '可爱的动漫少女，大眼睛，粉色头发，校园制服，樱花背景，日本动漫画风' },
        { id: '3', emoji: '🎨', title: '水彩艺术', prompt: '春天的田野，野花盛开，蝴蝶飞舞，水彩画风格，柔和色调，艺术感' },
        { id: '4', emoji: '🌃', title: '赛博朋克', prompt: '未来城市街道，霓虹灯招牌，雨夜反光，赛博朋克风格，高对比度，科幻感' },
        { id: '5', emoji: '🏔️', title: '自然风光', prompt: '雪山湖泊，倒影清晰，蓝天白云，4K高清，自然纪录片风格' }
      ])
    } finally {
      setLoadingInspirations(false)
    }
  }

  // 实时更新：收到 SSE 事件时更新图片状态
  useEffect(() => {
    const handler = (p: any) => {
      console.log('🔔 [图片生成] 收到事件更新:', p)
      
      const updateEntry = (prev: any[]) => prev.map((it: any) => {
        if (it.id !== p.id) {
          return it;
        }
        
        let outputImages: string[] = [];
        try {
          outputImages = typeof p.outputImages === 'string' ? JSON.parse(p.outputImages) : (Array.isArray(p.outputImages) ? p.outputImages : []);
        } catch (e) {
          console.error('❌ [SSE事件] updateEntry 解析 outputImages 失败:', e);
        }
            
        // 优先使用事件中的 outputImages，如果没有则保留原有的
        const urls = outputImages.length > 0 ? outputImages : (it.outputImages || []);
        const newStatus = p.status?.toUpperCase() || it.status;
            
        console.log('✅ [图片生成] SSE事件更新状态:', {
              id: p.id,
              oldStatus: it.status,
          newStatus: newStatus,
          imageCount: urls.length,
          hasOutputImages: outputImages.length > 0
        });
            
            return {
              ...it,
          id: p.id,
          prompt: p.prompt || it.prompt,
          createdAt: p.createdAt || it.createdAt,
          status: newStatus,
              url: urls[0] || it.url,
              outputImages: urls
        };
      });

      // 使用函数式更新，确保基于最新状态检查
      setGeneratedImages(prev => {
        const exists = prev.some((img: any) => img.id === p.id);
        const newStatus = p.status?.toUpperCase() || 'GENERATING';
        
      if (exists) {
          // 检查状态是否从非COMPLETED变为COMPLETED
          const oldItem = prev.find(it => it.id === p.id);
          const wasCompleted = oldItem?.status?.toUpperCase() === 'COMPLETED';
          const isNowCompleted = newStatus === 'COMPLETED';
          
          let outputImages: string[] = [];
          try {
            outputImages = typeof p.outputImages === 'string' ? JSON.parse(p.outputImages) : (Array.isArray(p.outputImages) ? p.outputImages : []);
          } catch (e) {
            console.error('❌ [SSE事件] 解析 outputImages 失败:', e);
          }
          
          // 如果状态变为COMPLETED且有图片，显示成功消息
          if (isNowCompleted && !wasCompleted && outputImages.length > 0) {
            message.success(`图片生成完成！共 ${outputImages.length} 张`);
          }
          
          return updateEntry(prev);
        }

        // 如果是新ID且列表中不存在，直接添加（可能是从其他页面或刷新后的事件）
        console.log('📥 [图片生成] 收到新任务的事件更新，直接添加:', p.id);
          let outputImages: string[] = [];
          try {
            outputImages = typeof p.outputImages === 'string' ? JSON.parse(p.outputImages) : (Array.isArray(p.outputImages) ? p.outputImages : []);
          } catch {}

        // 如果是COMPLETED状态且有图片，显示成功消息
        if (newStatus === 'COMPLETED' && outputImages.length > 0) {
          message.success(`图片生成完成！共 ${outputImages.length} 张`);
        }
        
        return [...prev, {
            id: p.id,
          prompt: p.prompt || '',
          createdAt: p.createdAt || new Date().toISOString(),
          status: newStatus,
          url: outputImages[0] || '',
          outputImages: outputImages
        }];
      });
    };

    onEvent('image_updated', handler);
  }, []); // 移除依赖，避免重复注册

  // Fallback轮询机制：定期检查图片状态，确保UI更新（防止SSE事件丢失）
  useEffect(() => {
    // 找到所有未完成的图片（包括 GENERATING、PENDING、PROCESSING 状态）
    const pendingImages = generatedImages.filter(
      img => {
        const status = img.status?.toUpperCase();
        return (status === 'GENERATING' || status === 'PENDING' || status === 'PROCESSING') && !img.url;
      }
    )

    if (pendingImages.length === 0) {
      return
    }

    console.log(`🔄 [图片生成] 开始定期检查 ${pendingImages.length} 个任务的状态...`)

    const checkStatus = async () => {
      for (const img of pendingImages) {
        try {
          const response = await apiClient.get(`/image/generations/${img.id}`)
          
          if (response.success && response.data?.imageGeneration) {
            const gen = response.data.imageGeneration
            const genStatus = gen.status?.toUpperCase();
            
            console.log(`📊 [定期检查] 任务 ${img.id} 状态: ${genStatus}`)
            
            // 检查是否完成
            if (genStatus === 'COMPLETED' && gen.outputImages) {
              console.log(`✅ [定期检查] 图片已完成: ${img.id}`)
              
              let outputImages: string[] = []
              try {
                outputImages = typeof gen.outputImages === 'string' 
                  ? JSON.parse(gen.outputImages) 
                  : (Array.isArray(gen.outputImages) ? gen.outputImages : [])
              } catch (e) {
                console.error('❌ [定期检查] 解析 outputImages 失败:', e)
              }
              
              if (outputImages.length > 0) {
                console.log(`✅ [定期检查] 更新图片状态，共 ${outputImages.length} 张图片`)
                console.log(`📸 [定期检查] 图片URLs:`, outputImages)

              setGeneratedImages(prev => prev.map(it => 
                it.id === img.id 
                    ? { 
                        ...it, 
                        status: 'COMPLETED', 
                        url: outputImages[0], 
                        outputImages: outputImages 
                      }
                  : it
              ))
                
                message.success(`图片生成完成！共 ${outputImages.length} 张`)
              }
            } else if (genStatus === 'FAILED') {
              // 处理失败状态
              console.log(`❌ [定期检查] 图片生成失败: ${img.id}`)
              setGeneratedImages(prev => prev.map(it => 
                it.id === img.id 
                  ? { ...it, status: 'FAILED', errorMessage: gen.errorMessage }
                  : it
              ))
            } else if (genStatus === 'PROCESSING' || genStatus === 'PENDING') {
              // 更新状态为 PROCESSING
              setGeneratedImages(prev => prev.map(it => 
                it.id === img.id 
                  ? { ...it, status: genStatus }
                  : it
              ))
            }
          }
        } catch (error) {
          console.error('❌ [定期检查] 查询失败:', error)
        }
      }
    }

    // 图片生成：10秒后开始轮询，每10秒检查一次（防止消耗服务器资源）
    const timer = setInterval(checkStatus, 10000)
    
    return () => clearInterval(timer)
  }, [generatedImages])

  // 组件卸载时清理临时预览项
  useEffect(() => {
    return () => {
      // 清理所有临时ID的预览项
      setGeneratedImages(prev => prev.filter(img => !img.id.startsWith('temp-')))
      console.log('🧹 [图片生成] 页面卸载，清理临时预览')
    }
  }, [])

  // 宽高比配置（带预览图标）
  const aspectRatioOptions = [
    { id: 'AUTO', name: '自动', icon: '🤖', description: 'AI智能选择' },
    { id: 'SQUARE', name: '1:1', icon: '⬜', description: '1024×1024' },
    { id: 'LANDSCAPE_16_9', name: '16:9', icon: '📺', description: '1920×1080' },
    { id: 'PORTRAIT_9_16', name: '9:16', icon: '📱', description: '1080×1920' },
    { id: 'LANDSCAPE_3_4', name: '4:3', icon: '🖼️', description: '1536×2048' },
    { id: 'PORTRAIT_4_3', name: '3:4', icon: '🎨', description: '2048×1536' }
  ]

  const calculateCost = () => {
    // 获取选中模型的基础费用
    const selectedModelData = imageModels.find(m => m.id === selectedModel);
    let cost = selectedModelData?.cost || 10; // 如果有模型配置就使用，否则默认10
    
    // 根据选项添加额外费用
    if (count > 1) cost += (count - 1) * 5
    if (removeWatermark) cost += 5
    return cost
  }

  const handleGenerate = async (values: any) => {
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTimeRef.current;
    
    // 检查是否在最小间隔内
    if (timeSinceLastClick < MIN_CLICK_INTERVAL && lastClickTimeRef.current > 0) {
      const remainingTime = Math.ceil((MIN_CLICK_INTERVAL - timeSinceLastClick) / 1000);
      message.warning(`请稍候 ${remainingTime} 秒后再试`);
      return;
    }
    
    // 更新最后点击时间
    lastClickTimeRef.current = now;
    
    const startTime = Date.now();
    console.log('\n' + '='.repeat(80));
    console.log('🎨 [前端] 图片生成请求开始');
    console.log('⏰ 时间:', new Date().toISOString());
    console.log('='.repeat(80));
    
    // 🔥 新增：检查积分是否足够
    const { user } = useAuthStore.getState();
    const cost = calculateCost();
    
    if (!user || (user.pointsBalance < cost && user.credits < cost)) {
      message.error(`积分不足！需要 ${cost} 积分，当前余额: ${user?.pointsBalance || 0} 积分`);
      console.error(`❌ 积分不足: 需要 ${cost}, 当前 ${user?.pointsBalance || 0}`);
      return;
    }
    
    console.log(`✅ 积分检查通过: 需要 ${cost}, 当前 ${user.pointsBalance}`);
    
    console.log('🔍 [前端] 当前 selectedModel:', selectedModel);
    console.log('🔍 [前端] selectedModel 类型:', typeof selectedModel);
    console.log('🔍 [前端] selectedModel 是否为空:', !selectedModel);
    console.log('🔍 [前端] selectedModel 是否等于空字符串:', selectedModel === '');
    console.log('🔍 [前端] 当前 imageModels:', imageModels.length, '个');
    console.log('🔍 [前端] imageModels 详情:', imageModels);
    
    // 🔥 验证模型是否已选择
    if (!selectedModel || selectedModel === '' || selectedModel === null || selectedModel === undefined) {
      message.error('请先选择生成模型');
      console.error('❌ [前端] 未选择模型');
      console.error('❌ [前端] selectedModel 值为:', selectedModel);
      console.error('❌ [前端] selectedModel 类型:', typeof selectedModel);
      console.error('❌ [前端] selectedModel === null:', selectedModel === null);
      console.error('❌ [前端] selectedModel === undefined:', selectedModel === undefined);
      console.error('❌ [前端] selectedModel === "":', selectedModel === '');
      console.error('❌ [前端] imageModels:', imageModels);
      return;
    }
    
    console.log('✅ [前端] 模型验证通过，selectedModel:', selectedModel);
    
    setLoading(true)
    
    // 🔥 清除之前的预览，避免重复显示
    setGeneratedImages([])
    console.log('🧹 [图片生成] 已清除历史预览')
    
    try {
      // 自动判断生成类型：暂时只支持文生图
      const actualGenerationType = 'TEXT_TO_IMAGE';
      console.log('🔍 生成类型:', actualGenerationType, '(文生图模式)');
      
      const payload: any = {
        prompt: values.prompt,
        model: selectedModel, // 🔥 添加模型参数
        type: actualGenerationType,
        aspectRatio,
        count,
        removeWatermark
      }
      // 若有参考图，附带到 payload
      if (refImageUrl) {
        payload.inputImage = refImageUrl
      }
      
             console.log('📊 [前端] 请求 payload:');
       console.log('   prompt:', payload.prompt?.substring(0, 50) + '...');
       console.log('   model:', payload.model);
       console.log('   model 类型:', typeof payload.model);
       console.log('   model 是否为空:', !payload.model);
       console.log('   type:', payload.type);
       console.log('   aspectRatio:', payload.aspectRatio);
       console.log('   count:', payload.count);
       console.log('   removeWatermark:', payload.removeWatermark);
       
       console.log('📊 [前端] 完整 payload:', JSON.stringify(payload, null, 2));
       console.log('📊 [前端] payload.model 详情:', payload.model);
       
       // 🔥 临时调试：检查 selectedModel 的值
       console.log('🔍 [前端] 调试信息:');
       console.log('   selectedModel:', selectedModel);
       console.log('   selectedModel 类型:', typeof selectedModel);
       console.log('   selectedModel === payload.model:', selectedModel === payload.model);
      
      console.log('📤 发送 API 请求: POST /image/generate');
      const apiStartTime = Date.now();
      
      const res = await apiClient.post('/image/generate', payload, { timeout: 120000 })
      const apiTime = Date.now() - apiStartTime;
      
      console.log(`✅ API 响应成功 (耗时: ${apiTime}ms)`);
      console.log('📥 响应数据:', {
        success: res.success,
        hasImageGeneration: !!res.data?.imageGeneration,
        generationId: res.data?.imageGeneration?.id,
        status: res.data?.imageGeneration?.status,
        cost: res.data?.imageGeneration?.cost,
        count: res.data?.imageGeneration?.count
      });
      
      if (res.success && res.data?.imageGeneration) {
        // 直接使用真实ID，无需临时ID替换
        const img = res.data.imageGeneration
        
        // 直接添加真实ID的记录（检查是否已存在，避免重复）
        setGeneratedImages(prev => {
          const exists = prev.some(item => item.id === img.id);
          if (exists) {
            console.log('⚠️ [图片生成] 记录已存在，更新状态:', img.id);
            return prev.map(item => 
              item.id === img.id 
            ? {
                    ...item,
          prompt: img.prompt,
          aspectRatio: img.aspectRatio,
          removeWatermark: img.removeWatermark,
          createdAt: img.createdAt,
                    status: img.status || 'GENERATING'
              }
            : item
            );
          }
          // 如果不存在，添加新记录
          return [...prev, {
            id: img.id,
            url: '',
                  prompt: img.prompt,
                  aspectRatio: img.aspectRatio,
                  removeWatermark: img.removeWatermark,
                  createdAt: img.createdAt,
            status: img.status || 'GENERATING',
            outputImages: []
          }];
        });
        
        console.log('✅ 任务创建成功，真实ID:', img.id);
        message.success('任务创建成功，正在生成中...');
        
        // 移动端自动切换到结果标签
        setMobileTab('result')
        
        // 本地乐观扣费，SSE 会以绝对值回补
        try {
          const { user, updateUser } = useAuthStore.getState()
          if (user?.pointsBalance !== undefined && (img?.cost !== undefined)) {
            const oldCredits = user.pointsBalance || 0;
            const cost = img.cost || 0;
            const newCredits = Math.max(0, oldCredits - cost);
            updateUser({ pointsBalance: newCredits })
            console.log(`💰 本地积分更新: ${oldCredits} → ${newCredits} (扣除: ${cost})`);
          }
        } catch (creditsError) {
          console.error('❌ 本地积分更新失败:', creditsError);
        }
        
        const totalTime = Date.now() - startTime;
        console.log(`🎉 请求处理完成 (总耗时: ${totalTime}ms)`);
        console.log('='.repeat(80) + '\n');
      } else {
        throw new Error(res.message || '生成创建失败')
      }
    } catch (e: any) {
      const totalTime = Date.now() - startTime;
      console.error('\n' + '='.repeat(80));
      console.error('❌ [前端] 图片生成请求失败');
      console.error('⏱️  失败前耗时:', totalTime + 'ms');
      console.error('='.repeat(80));
      
      console.error('🔍 错误详情:');
      console.error('  错误类型:', e?.constructor?.name || 'Unknown');
      console.error('  错误消息:', e?.message || 'Unknown error');
      
      if (e?.response) {
        console.error('📡 API 响应错误:');
        console.error('  状态码:', e.response.status);
        console.error('  状态文本:', e.response.statusText);
        console.error('  响应数据:', e.response.data);
        console.error('  响应数据（JSON）:', JSON.stringify(e.response.data, null, 2));
      }
      
      if (e?.stack) {
        console.error('📚 错误堆栈:', e.stack);
      }
      
      console.error('='.repeat(80) + '\n');
      
      // 检测积分不足错误（处理 500 错误中的积分不足情况）
      let errorMessage = e?.message || '生成失败，请重试';
      let isInsufficientCredits = false;
      let allMessages: string[] = [];
      
      // 收集所有可能的错误消息
      if (e?.message) {
        allMessages.push(String(e.message));
      }
      
      if (e?.response?.data?.message) {
        allMessages.push(String(e.response.data.message));
      }
      
      if (e?.response?.data?.error) {
        allMessages.push(String(e.response.data.error));
      }
      
      // 也检查整个响应数据对象（转为字符串）
      if (e?.response?.data) {
        try {
          const responseStr = JSON.stringify(e.response.data);
          allMessages.push(responseStr);
        } catch (err) {
          // 忽略序列化错误
        }
      }
      
      // 合并所有消息进行检测
      const combinedMessage = allMessages.join(' ').toLowerCase();
      
      console.log('🔍 [错误检测] 所有错误消息:', allMessages);
      console.log('🔍 [错误检测] 合并消息:', combinedMessage);
      
      // 检测积分不足关键词
      if (combinedMessage.includes('insufficient credits') ||
          combinedMessage.includes('积分不足') ||
          combinedMessage.includes('credits and points') ||
          (combinedMessage.includes('insufficient') && combinedMessage.includes('credit'))) {
        isInsufficientCredits = true;
        console.log('✅ [错误检测] 检测到积分不足错误（关键词匹配）');
      }
      
      // 尝试解析嵌套的 JSON 字符串（如：{"code":-1,"msg":"insufficient credits"}）
      if (!isInsufficientCredits) {
        try {
          const messageStr = allMessages.join(' ');
          // 匹配所有 JSON 对象
          const jsonMatches = messageStr.match(/\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g);
          if (jsonMatches) {
            for (const jsonStr of jsonMatches) {
              try {
                const nestedJson = JSON.parse(jsonStr);
                // 检查各种可能的字段名
                const msgValue = nestedJson?.msg || nestedJson?.message || nestedJson?.error;
                if (msgValue) {
                  const nestedMsg = String(msgValue).toLowerCase();
                  if (nestedMsg.includes('insufficient credits') ||
                      nestedMsg.includes('积分不足') ||
                      nestedMsg.includes('credit')) {
                    isInsufficientCredits = true;
                    console.log('✅ [错误检测] 检测到积分不足错误（JSON解析）:', nestedMsg);
                    break;
                  }
                }
              } catch (parseErr) {
                // 继续尝试下一个
              }
            }
          }
        } catch (parseError) {
          console.log('❌ [错误检测] JSON解析失败:', parseError);
        }
      }
      
      console.log('🔍 [错误检测] 最终结果 - 积分不足:', isInsufficientCredits);
      
      // 显示错误提示
      if (isInsufficientCredits) {
        Modal.confirm({
          title: '积分不足',
          content: '您的积分不足，无法生成图片。是否前往充值？',
          okText: '去充值',
          cancelText: '取消',
          onOk: () => {
            // 导航到充值页面
            navigate('/payment');
          }
        });
      } else {
        message.error(errorMessage);
      }
    } finally {
      setLoading(false)
    }
  }

  const downloadImage = (imageUrl: string, filename: string) => {
    const link = document.createElement('a')
    link.href = imageUrl
    link.download = filename
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className="min-h-screen w-full relative">
      {/* 内容区域 - 应用统一样式 */}
      <div className="unified-container relative z-10 py-2 md:py-6">
        <motion.div
          {...COMMON_STYLES.pageEnter}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex flex-col"
        >
          {/* 头部设计 - 左右结构 */}
          <div className="w-full">
            <motion.div
              {...COMMON_STYLES.pageEnter}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
            >
              {/* 左侧：标题和副标题 */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl">🎨</span>
                  <h1 className="unified-title-h1 mb-0 bg-gradient-to-r from-pink-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
                  AI 图像生成
                  </h1>
                </div>
                <Paragraph className="unified-text-secondary text-base md:ml-12 !mb-0">
                用创意文字，让AI绘制你梦想中的画面
              </Paragraph>
              </div>
              
              {/* 右侧：特色标签 */}
              <div className="flex flex-wrap gap-2 md:justify-end">
                <Tag icon={<ThunderboltOutlined />} className="unified-tag bg-gradient-to-r from-purple-50 to-purple-100 text-purple-700">
                  秒级生成
                </Tag>
                <Tag icon={<EyeOutlined />} className="unified-tag bg-gradient-to-r from-pink-50 to-pink-100 text-pink-700">
                  高清画质
                </Tag>
                <Tag icon={<HeartOutlined />} className="unified-tag bg-gradient-to-r from-red-50 to-red-100 text-red-700">
                  无限创意
                </Tag>
              </div>
            </motion.div>
          </div>


          {/* 主内容区 - 左右2:1布局 */}
          <div className="w-full h-full">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch h-full">
              {/* 左侧：输入窗口 - 占2列 (2/3) */}
              <div className="lg:col-span-2">
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sticky top-6"
                >
                  {/* 移动端顶部装饰 */}
                  <div className="md:hidden bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 h-1.5 w-full rounded-full mb-6"></div>
                

                         <Form
                           form={form}
                           layout="vertical"
                           onFinish={handleGenerate}
                  className="flex flex-col"
                >
                  {/* 参考图预览卡片 - 功能暂未实现，已隐藏 */}
                  {false && refImageFile && (
                    <div className="mb-4 p-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl border-2 border-pink-200 shadow-md hover:shadow-lg transition-all duration-300">
                      <div className="flex items-center justify-between mb-2">
                        <Text className="text-sm font-bold text-pink-700 flex items-center gap-2">
                          📸 参考图片
                        </Text>
                        <div className="flex items-center gap-1 px-2 py-1 bg-green-500 text-white rounded-full text-xs font-medium">
                          <span>✓</span>
                          <span>已上传</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="relative w-20 h-20 flex-shrink-0">
                          <img 
                            src={URL.createObjectURL(refImageFile)} 
                            alt="参考图" 
                            className="w-full h-full object-cover rounded-xl shadow-md"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <Text className="block text-sm font-semibold truncate text-gray-800">{refImageFile.name}</Text>
                          <Text type="secondary" className="text-xs text-gray-500">
                            {(refImageFile.size / 1024).toFixed(2)} KB
                          </Text>
                      </div>
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            setRefImageFile(null);
                          }}
                          className="rounded-xl bg-red-50 border-red-200 text-red-600 hover:bg-red-100 hover:border-red-300 transition-all duration-200 shadow-sm"
                        >
                          删除
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* 热门创作灵感卡片 - 从API动态加载 */}
                  <div className="mb-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl p-4 border-2 border-pink-200 shadow-md hover:shadow-lg transition-all duration-300">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <BulbOutlined className="text-lg text-pink-600" />
                        <Text className="text-sm font-bold text-pink-700">热门创作灵感</Text>
                        {inspirations.length > 0 && (
                          <Tag color="pink" className="text-xs">{inspirations.length}个</Tag>
                        )}
                      </div>
                      <Text className="text-xs text-pink-600">点击即可使用 →</Text>
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-2 inspiration-scroll inspiration-scroll-pink hide-scrollbar">
                      {inspirations.length > 0 ? (
                        inspirations.map((item: any) => (
                          <span
                            key={item.id}
                            className="inline-flex items-center px-3 py-2 text-xs bg-white hover:bg-gradient-to-r hover:from-pink-50 hover:to-purple-50 text-gray-700 hover:text-pink-700 rounded-xl cursor-pointer transition-all duration-200 border border-pink-200 hover:border-pink-300 shadow-sm hover:shadow-md font-medium hover:scale-105 whitespace-nowrap overflow-hidden text-ellipsis max-w-[200px] flex-shrink-0"
                            onClick={async () => {
                              form.setFieldsValue({ prompt: item.prompt })
                              setPromptValue(item.prompt)
                              message.success(`已应用"${item.title}"灵感 ✨`)
                              // 记录使用次数
                              try {
                                await apiClient.post(`/inspiration/${item.id}/use`)
                              } catch (error) {
                                console.error('记录使用失败:', error)
                              }
                              setMobileTab('create')
                              setTimeout(() => {
                                try {
                                  promptInputRef?.current?.focus?.({ cursor: 'end' })
                                  promptInputRef?.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
                                } catch {}
                              }, 0)
                            }}
                            title={`${item.title}\n\n${item.prompt.substring(0, 100)}...`}
                          >
                            {item.title}
                          </span>
                        ))
                      ) : (
                        <Text type="secondary" className="text-xs">
                          {loadingInspirations ? '加载中...' : '暂无灵感，请在管理后台添加'}
                        </Text>
                      )}
                    </div>
                  </div>

                  {/* 主输入区域卡片 */}
                  <div className="relative mb-4 bg-gray-50 rounded-xl p-4 border-2 border-gray-200 hover:border-pink-300 focus-within:border-pink-400 transition-all duration-300 shadow-md hover:shadow-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                      <span className="text-lg">✍️</span>
                      <Text className="text-sm font-bold text-gray-700">创作描述</Text>
                      </div>
                      <Button
                        type="link"
                        size="small"
                        icon={<BulbOutlined />}
                        loading={polishing}
                        onClick={async () => {
                          const currentPrompt = form.getFieldValue('prompt');
                          if (!currentPrompt || !currentPrompt.trim()) {
                            message.warning('请先输入描述内容');
                            return;
                          }
                          
                          try {
                            setPolishing(true);
                            const response = await textService.polishPrompt(currentPrompt, 'image');
                            if (response.success) {
                              form.setFieldsValue({ prompt: response.data.polishedText });
                              setPromptValue(response.data.polishedText);
                              
                              const cost = response.data?.cost || 0;
                              message.success(cost > 0 ? `润色成功，消耗 ${cost} 积分` : '润色成功');
                              
                              // 🔥 修复：润色成功后立即刷新用户积分（延迟一下确保后端事件已发送）
                              const { refreshUser } = useAuthStore.getState();
                              // 立即刷新一次
                              try {
                                await refreshUser();
                                console.log('✅ [ImagePage] 润色成功，用户积分已刷新');
                              } catch (refreshError) {
                                console.warn('⚠️ [ImagePage] 刷新用户积分失败:', refreshError);
                              }
                              // 延迟再刷新一次，确保后端事件处理完成
                              setTimeout(async () => {
                                try {
                                  await refreshUser();
                                  console.log('✅ [ImagePage] 延迟刷新用户积分完成');
                                } catch (refreshError) {
                                  console.warn('⚠️ [ImagePage] 延迟刷新用户积分失败:', refreshError);
                                }
                              }, 500);
                            }
                          } catch (error: any) {
                            message.error(error.response?.data?.message || '润色失败');
                          } finally {
                            setPolishing(false);
                          }
                        }}
                        className="text-blue-500 hover:text-blue-600"
                      >
                        {polishing ? '文案润色中...' : '文案润色'}
                      </Button>
                    </div>

                  <Form.Item
                    name="prompt"
                    rules={[{ required: true, message: '请输入图像描述' }]}
                      className="mb-0"
                  >
                    <div className="relative">
                    <TextArea
                          ref={promptInputRef}
                        rows={8}
                        placeholder="描述你想要的图像内容... 比如：一只可爱的小猫在花园里玩耍，阳光明媚，4K高清画质"
                        className="border-0 shadow-none resize-none focus:shadow-none pr-24 bg-white text-gray-700 placeholder-gray-400 rounded-xl"
                          // 给左侧缩略图腾出空间
                        style={{ 
                            resize: 'none', boxShadow: 'none', border: 'none', outline: 'none',
                            paddingBottom: '50px', fontSize: '15px', background: 'white', lineHeight: '1.8', padding: '16px 16px 16px 104px'
                          }}
                          value={promptValue}
                          onChange={(e) => setPromptValue(e.target.value)}
                      maxLength={2000}
                        showCount={false}
                    />
                      {/* 左上角 上传参考图（与视频页一致风格） */}
                      <div className="absolute top-2 left-2 z-10">
                        <div className="w-16 h-16 border border-gray-200 rounded-md overflow-hidden relative group">
                          <Upload
                            className="!m-0 !p-0 w-full h-full"
                            style={{ width: '100%', height: '100%', display: 'block' }}
                            accept="image/jpeg,image/png,image/webp"
                            showUploadList={false}
                            beforeUpload={(file) => {
                              setInlineError(null)
                              const isAllowed = ['image/jpeg','image/png','image/webp'].includes(file.type)
                              if (!isAllowed) {
                                message.error('仅支持 JPG/PNG/WEBP')
                                return Upload.LIST_IGNORE as any
                              }
                              const maxSize = 5 * 1024 * 1024
                              if (file.size > maxSize) {
                                message.error('图片大小不能超过 5MB')
                                return Upload.LIST_IGNORE as any
                              }
                              setUploadingRef(true)
                              return true
                            }}
                            customRequest={async (options: any) => {
                              try {
                                const { file, onSuccess } = options
                                const formData = new FormData()
                                formData.append('file', file as File)
                                const res = await apiClient.post('/video/upload-image', formData, {
                                  headers: { 'Content-Type': 'multipart/form-data' }
                                })
                                if (res.success && res.data?.url) {
                                  setRefImageUrl(res.data.url)
                                  if (typeof onSuccess === 'function') onSuccess({})
                                } else {
                                  throw new Error(res.message || '上传失败')
                                }
                              } catch (err: any) {
                                setInlineError(err?.message || '上传失败')
                                if (options && typeof options.onError === 'function') {
                                  options.onError(err)
                                }
                              } finally {
                                setUploadingRef(false)
                              }
                            }}
                          >
                            {refImageUrl ? (
                              <div className="w-full h-full cursor-pointer" onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                Modal.info({
                                  title: null,
                                  centered: true,
                                  icon: null,
                                  content: (
                                    <div className="relative inline-block bg-black/20 border border-white/30 rounded-md" style={{ width: 'fit-content' }}>
                                      <button
                                        type="button"
                                        onClick={() => Modal.destroyAll()}
                                        className="!m-0 !p-0 absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 hover:bg-black/70 text-white flex items-center justify-center"
                                        aria-label="关闭"
                                      >
                                        ×
                                      </button>
                                      <img
                                        src={refImageUrl}
                                        alt="参考图预览"
                                        className="object-contain rounded-md"
                                        style={{ background: 'transparent', display: 'block', maxWidth: '95vw', maxHeight: '90vh' }}
                                      />
                                    </div>
                                  ),
                                  width: undefined,
                                  footer: null,
                                  styles: { body: { padding: 0, background: 'transparent' }, content: { padding: 0, background: 'transparent' } },
                                  bodyStyle: { padding: 0 },
                                  maskClosable: true
                                })
                              }}>
                                <img src={refImageUrl} alt="参考图" className="w-full h-full object-cover" />
                                <button
                                  type="button"
                                  className="absolute -top-2 -right-2 w-6 h-6 bg-white/90 text-gray-600 rounded-full shadow flex items-center justify-center hover:bg-white"
                                  onClick={(e) => { e.stopPropagation(); setRefImageUrl(null); setInlineError(null) }}
                                  aria-label="删除参考图"
                                >
                                  ×
                                </button>
                              </div>
                            ) : (
                              <div className="w-full h-full bg-gray-50 hover:bg-blue-50 cursor-pointer transition-all duration-200 flex items-center justify-center !m-0 !p-0">
                                <div className="flex flex-col items-center justify-center gap-0.5">
                                  {uploadingRef ? (
                                    <div className="flex flex-col items-center justify-center text-blue-500 gap-0.5">
                                      <div className="animate-spin text-2xl leading-none">⏳</div>
                                      <div className="text-[11px] leading-none">上传中</div>
                                    </div>
                                  ) : (
                                    <div className="flex flex-col items-center justify-center gap-0.5">
                                      <div className="text-3xl text-gray-600 group-hover:text-blue-500 transition-colors font-semibold leading-none">+</div>
                                      <div className="text-[11px] text-gray-400 leading-none">上传参考图</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </Upload>
                        </div>
                      </div>
                    </div>
                  </Form.Item>
                  {/* 预览弹窗：已改为与视频页相同的 Modal.info 方案，故移除冗余状态弹窗 */}
                  {inlineError && (
                    <div className="mt-1 text-xs text-red-500 leading-snug">{inlineError}</div>
                  )}

                      {/* 生成按钮 */}
                      <div className="absolute bottom-8 right-8">
                        {(() => {
                          const { user } = useAuthStore.getState();
                          const cost = calculateCost();
                          const isPointsInsufficient = !user || (user.pointsBalance < cost && user.credits < cost);
                          
                          return (
                            <>
                              {isPointsInsufficient && (
                                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                  <div className="flex items-center gap-2 text-yellow-800">
                                    <span>⚠️</span>
                                    <span className="text-sm font-medium">积分不足</span>
                                  </div>
                                  <div className="text-xs text-yellow-700 mt-1">
                                    生成需要 {cost} 积分，当前余额: {user?.pointsBalance || 0} 积分
                                  </div>
                                  <Button 
                                    size="small" 
                                    type="primary" 
                                    onClick={() => navigate('/payment')}
                                    className="mt-2"
                                  >
                                    立即充值
                                  </Button>
                                </div>
                              )}
                              
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={loading}
                          disabled={loading || uploadingRef || !selectedModel || imageModels.length === 0 || loadingModels || isPointsInsufficient}
                          className={`unified-button h-12 px-6 py-3 ${
                            (!selectedModel || imageModels.length === 0 || loadingModels || isPointsInsufficient) 
                              ? 'bg-gray-300 cursor-not-allowed' 
                              : ''
                          }`}
                          style={{
                            background: (uploadingRef || !selectedModel || imageModels.length === 0 || loadingModels || isPointsInsufficient) 
                              ? '#d1d5db' 
                              : GlobalStyles.colors.primary
                          }}
                        >
                          {loading ? (
                            <span className="flex items-center gap-2">
                              <span className="animate-spin">⏳</span>
                              生成中...
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              <span>🎨</span>
                              {uploadingRef 
                                ? '参考图上传中...'
                                : (!selectedModel || imageModels.length === 0 
                                   ? '请先选择模型' 
                                   : (isPointsInsufficient ? '积分不足' : '开始生成'))}
                            </span>
                          )}
                        </Button>
                            </>
                          );
                        })()}
                      </div>
                  </div>

                  {/* 参数设置卡片 - 应用统一样式 */}
                  <div className="unified-card">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-lg">⚙️</span>
                      <Text className="unified-text-primary font-bold">生成参数</Text>
                    </div>
                        <div className="flex items-center gap-3 flex-nowrap overflow-x-auto">
                          {/* 参考图按钮 - 功能暂未实现，已禁用 */}
                          {/* TODO: 实现图生图功能后启用此按钮 */}
                          {false && (
                          <div className="relative group flex-shrink-0">
                          <Upload
                            accept="image/*"
                            showUploadList={false}
                            beforeUpload={(file) => {
                              setRefImageFile(file);
                              return false;
                            }}
                          >
                            <Button
                                type="default"
                      size="small"
                                className={`h-10 px-4 rounded-xl text-sm font-semibold border-2 whitespace-nowrap shadow-lg transition-all duration-300 hover:scale-105 ${
                                  refImageFile 
                                    ? 'bg-gradient-to-r from-pink-100 to-purple-100 text-pink-700 border-pink-300 shadow-pink-200' 
                                    : 'bg-white text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-pink-50 hover:border-pink-200'
                                }`}
                              >
                                上传参考图
                              </Button>
                            </Upload>
                            {/* 上传提示 */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                              支持 JPG/PNG，推荐尺寸 1024x1024
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                            </div>
                  </div>
                          )}


                          {/* 图片比例选择 */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-sm text-gray-600 font-semibold">比例:</span>
                            <Button
                              type="default"
                              size="small"
                              className="h-10 px-4 rounded-xl text-sm font-semibold bg-white text-gray-700 border-2 border-gray-200 hover:bg-gradient-to-r hover:from-gray-50 hover:to-pink-50 hover:border-pink-200 whitespace-nowrap shadow-lg transition-all duration-300 hover:scale-105"
                              onClick={() => {
                                // 循环切换比例
                                const ratios: AspectRatio[] = ['AUTO', 'SQUARE', 'LANDSCAPE_16_9', 'PORTRAIT_9_16', 'LANDSCAPE_3_4', 'PORTRAIT_4_3']
                                const currentIndex = ratios.indexOf(aspectRatio)
                                const nextIndex = (currentIndex + 1) % ratios.length
                                setAspectRatio(ratios[nextIndex])
                              }}
                            >
                                {aspectRatio === 'AUTO' ? '自动' :
                                 aspectRatio === 'SQUARE' ? '1:1' :
                                 aspectRatio === 'LANDSCAPE_16_9' ? '16:9' :
                                 aspectRatio === 'PORTRAIT_9_16' ? '9:16' :
                                 aspectRatio === 'LANDSCAPE_3_4' ? '4:3' :
                                 aspectRatio === 'PORTRAIT_4_3' ? '3:4' : '自动'}
                            </Button>
                  </div>

                          {/* 生成数量 */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-sm text-gray-600 font-semibold">数量:</span>
                            <Button
                              type="default"
                              size="small"
                              className="h-10 px-4 rounded-xl text-sm font-semibold bg-white text-gray-700 border-2 border-gray-200 hover:bg-gradient-to-r hover:from-gray-50 hover:to-pink-50 hover:border-pink-200 whitespace-nowrap shadow-lg transition-all duration-300 hover:scale-105"
                              onClick={() => {
                                // 切换数量
                                setCount(count === 1 ? 2 : count === 2 ? 3 : count === 3 ? 4 : 1);
                              }}
                            >
                              {count}张
                            </Button>
                          </div>

                          {/* 去水印选项 */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-sm text-gray-600 font-semibold">去水印:</span>
                            <Button
                              type="default"
                              size="small"
                              className={`h-10 px-4 rounded-xl text-sm font-semibold border-2 whitespace-nowrap shadow-lg transition-all duration-300 hover:scale-105 ${
                                removeWatermark 
                                  ? 'bg-gradient-to-r from-pink-100 to-purple-100 text-pink-700 border-pink-300 shadow-pink-200' 
                                  : 'bg-white text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-pink-50 hover:border-pink-200'
                              }`}
                              onClick={() => setRemoveWatermark(!removeWatermark)}
                            >
                              {removeWatermark ? '已开启' : '未开启'}
                            </Button>
                      </div>

                          {/* AI模型选择 - 最后位置 */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-sm text-gray-600 font-semibold whitespace-nowrap">模型:</span>
                            <Select
                              value={selectedModel}
                              onChange={(value) => setSelectedModel(value)}
                              size="small"
                              className="min-w-[160px] max-w-[200px]"
                              style={{ height: '40px' }}
                              loading={loadingModels}
                              placeholder={imageModels.length === 0 ? "请管理员配置API服务商" : "选择模型"}
                              disabled={imageModels.length === 0}
                              styles={{ popup: { root: { minWidth: 200 } } }}
                            >
                              {imageModels.length === 0 ? (
                                <Select.Option value="" disabled>
                                  <div className="flex items-center gap-2 text-gray-400">
                                    <InfoCircleOutlined />
                                    <span className="text-sm">请管理员在控制台配置API服务商</span>
                                  </div>
                                </Select.Option>
                              ) : (
                                imageModels.map((model) => {
                                  return (
                                    <Select.Option key={model.id} value={model.id}>
                                      <div className="flex items-center justify-between w-full py-1">
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                          <span className="font-medium text-gray-800 text-sm truncate">{model.name}</span>
                                          <span className="text-xs text-gray-500 truncate">({model.providerName})</span>
                                        </div>
                                        <span className="text-xs text-blue-500 font-semibold ml-2 flex-shrink-0">{model.cost}积分</span>
                                      </div>
                                    </Select.Option>
                                  );
                                })
                              )}
                            </Select>
                          </div>
                    </div>
                  </div>
                </Form>
              </motion.div>
            </div>

            {/* 右侧：生成预览 - 占1列 (1/3) */}
            <div className="lg:col-span-1 hidden md:block">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sticky top-6 flex flex-col"
              >
                  <div className="flex items-center justify-between mb-3 py-0 min-h-[40px]">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">🖼️</span>
                    <Title level={4} className="!mb-0 font-bold">生成预览</Title>
                  </div>
                  <Space>
                    <Button 
                      icon={<HistoryOutlined />}
                      onClick={() => navigate('/history')}
                      size="small"
                      className="rounded-lg shadow-sm hover:shadow-md transition-all"
                    >
                      <span className="hidden md:inline">历史</span>
                      <span className="md:hidden">历史记录</span>
                    </Button>
                  </Space>
                </div>

                <div className="flex-1 flex flex-col -mt-2">
                <AnimatePresence mode="wait">
                  {generatedImages.length === 0 ? (
                    <motion.div
                      key="empty"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-center py-16 px-8 flex-1 flex flex-col justify-center"
                    >
                      <motion.div
                        animate={{ 
                          y: [0, -10, 0],
                          scale: [1, 1.05, 1],
                        }}
                        transition={{ 
                          duration: 3,
                          repeat: Infinity,
                          repeatType: "reverse"
                        }}
                        className="inline-block text-8xl"
                      >
                        🎨✨
                      </motion.div>
                      <Title level={3} className="text-gray-600 mb-4 font-bold">
                        开启你的创作之旅
                      </Title>
                      <Text type="secondary" className="text-lg mb-6 max-w-md">
                        输入你的创意灵感，让AI为你生成精彩的图像内容
                      </Text>
                      <div className="flex items-center gap-2 justify-center flex-nowrap">
                        <span className="px-2 py-1 bg-pink-100 text-pink-600 rounded-full text-xs whitespace-nowrap">🎨创意无限</span>
                        <span className="px-2 py-1 bg-purple-100 text-purple-600 rounded-full text-xs whitespace-nowrap">⚡快速生成</span>
                        <span className="px-2 py-1 bg-blue-100 text-blue-600 rounded-full text-xs whitespace-nowrap">🎯精准控制</span>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="results"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      className="space-y-4 flex-1 flex flex-col"
                    >
                      {/* 图片展示区域 */}
                        <div className={`grid gap-4 ${
                        count === 1 ? 'grid-cols-1' : 
                        count === 2 ? 'grid-cols-1 md:grid-cols-2' : 
                        'grid-cols-1 md:grid-cols-2'
                      }`} style={{ marginTop: '24px' }}>
                        {generatedImages.map((image, index) => (
                          <motion.div
                            key={image.id}
                            initial={{ opacity: 0, scale: 0.8, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                            className="group relative"
                          >
                              {image.status === 'GENERATING' || !image.url ? (
                                // 🎨 生成中预览卡片 - 美化版
                                <div className="relative rounded-2xl overflow-hidden shadow-2xl min-h-[280px]">
                                  {/* 梦幻渐变背景 */}
                                  <div className="absolute inset-0 bg-gradient-to-br from-pink-300 via-purple-300 to-indigo-400 opacity-90 animate-pulse"></div>
                                  <div className="absolute inset-0 bg-gradient-to-tl from-yellow-200/40 via-transparent to-pink-200/40 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                                  
                                  {/* 艺术粒子效果 */}
                                  <div className="absolute inset-0 overflow-hidden">
                                    <div className="absolute top-1/4 left-1/4 w-3 h-3 bg-white rounded-full animate-ping opacity-60"></div>
                                    <div className="absolute top-2/3 right-1/3 w-2 h-2 bg-yellow-100 rounded-full animate-ping opacity-50" style={{ animationDelay: '0.2s' }}></div>
                                    <div className="absolute bottom-1/4 left-2/3 w-2 h-2 bg-pink-100 rounded-full animate-ping opacity-55" style={{ animationDelay: '0.4s' }}></div>
                                    <div className="absolute top-1/2 right-1/4 w-1.5 h-1.5 bg-purple-100 rounded-full animate-ping opacity-65" style={{ animationDelay: '0.6s' }}></div>
                                  </div>
                                  
                                  {/* 内容区 */}
                                  <div className="relative backdrop-blur-sm bg-white/15 flex flex-col items-center justify-center p-8 text-center h-full min-h-[280px]">
                                    {/* 画笔/调色板图标 */}
                                    <div className="relative mb-5">
                                      <div className="w-14 h-14 bg-gradient-to-br from-white to-pink-50 rounded-2xl shadow-lg flex items-center justify-center transform hover:rotate-12 transition-all duration-300">
                                        <span className="text-3xl">🎨</span>
                                      </div>
                                      {/* 旋转光圈 */}
                                      <div className="absolute -inset-2 border-3 border-white/40 border-t-white rounded-full animate-spin"></div>
                                    </div>
                                    
                                    {/* 主标题 */}
                                    <div className="text-xl font-bold text-white mb-2 drop-shadow-lg">
                                      ✨ 正在为你绘制灵感
                                    </div>
                                    
                                    {/* 副标题 */}
                                    <div className="text-sm text-white/95 mb-3 font-medium drop-shadow">
                                      🖌️ AI 正在挥洒创意的色彩
                                    </div>
                                    
                                    {/* 等待提示 */}
                                    <div className="text-xs text-white/85 mb-3 flex items-center gap-1.5">
                                      <span className="animate-pulse text-base">⏳</span>
                                      <span>片刻等待，艺术即将诞生</span>
                                    </div>
                                    
                                    {/* 底部提示 */}
                                    <div className="mt-2 px-3 py-1.5 bg-white/95 rounded-full text-xs text-indigo-600 font-medium shadow-lg backdrop-blur">
                                      💡 预览将自动保存至历史记录
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                // 完成的结果卡片
                                <div className="relative rounded-lg overflow-hidden bg-white shadow-md">
                                  <Image 
                                    src={image.url} 
                                    alt={`Generated ${index + 1}`}
                                    className="w-full h-auto block"
                                    style={{ 
                                      objectFit: 'cover',
                                      display: 'block',
                                      backgroundColor: 'transparent'
                                    }}
                                    preview={{
                                      mask: (
                                        <div className="flex flex-col items-center gap-2">
                                          <EyeOutlined style={{ fontSize: '24px' }} />
                                          <span>预览</span>
                                        </div>
                                      )
                                    }}
                                  />
                                </div>
                              )}
                          </motion.div>
                        ))}
                      </div>

                      {/* 底部操作按钮 */}
                      <div className="flex justify-center space-x-3 mt-4">
                        <Button
                          type="primary"
                          icon={<DownloadOutlined />}
                          onClick={async () => {
                            try {
                              message.loading('正在下载图片...', 0)
                              
                              for (let i = 0; i < generatedImages.length; i++) {
                                const image = generatedImages[i]
                                if (image.url) {
                                  await new Promise(resolve => setTimeout(resolve, i * 300))
                                  await downloadImage(image.url, `image-${Date.now()}-${i + 1}.jpg`)
                                }
                              }
                              
                              message.destroy()
                              message.success('下载完成')
                            } catch (error) {
                              message.destroy()
                              message.error('下载失败，请重试')
                            }
                          }}
                          disabled={!generatedImages.some(img => img.url)}
                          className="rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                        >
                          下载{count > 1 ? '全部' : ''}
                        </Button>
                        <Button
                          icon={<ReloadOutlined />}
                          onClick={() => {
                            setGeneratedImages([])
                            form.resetFields()
                            message.info('已清空，可以重新生成')
                          }}
                          className="rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                        >
                          重新生成
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                </div>

                  {/* 创作提示卡片 - 独立模块 */}
                  <div className="mt-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border-2 border-amber-200 shadow-md hover:shadow-lg transition-all duration-300">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-2xl">💡</span>
                      <Text className="text-sm font-bold text-amber-800">创作提示</Text>
                    </div>
                    <ul className="text-sm text-amber-700 space-y-2.5 mb-0 leading-relaxed">
                      <li className="flex items-start gap-2">
                        <span className="text-amber-500 mt-0.5">✓</span>
                        <span>描述要具体、生动，包含场景、光线等细节</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-500 mt-0.5">✓</span>
                        <span>避免使用版权人物、品牌名称</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-500 mt-0.5">✓</span>
                        <span>添加艺术风格可获得更好效果（如：水彩、油画）</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-500 mt-0.5">✓</span>
                        <span>简单场景生成速度更快</span>
                      </li>
                    </ul>
                  </div>
              </motion.div>
              </div>
            </div>
          </div>

            {/* 底部创作风格区域 - 移动端隐藏 */}
            <div className="w-full mt-20 hidden md:block">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <div className="text-center mb-10">
              <Title level={2} className="mb-3">
                <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent">
                  ✨ 热门创作风格
                </span>
              </Title>
              <Text type="secondary" className="text-lg text-gray-600">💡 点击卡片快速开始创作，激发你的灵感</Text>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { 
                  style: '写实摄影',
                  prompt: '一位优雅的女性，专业摄影，柔和自然光，浅色背景，高清细节，时尚杂志风格',
                  emoji: '📸',
                  gradient: 'from-blue-50 to-cyan-50',
                  borderColor: 'border-blue-200'
                },
                {
                  style: '动漫风格',
                  prompt: '可爱的动漫少女，大眼睛，粉色头发，校园制服，樱花背景，日本动漫画风',
                  emoji: '🎌',
                  gradient: 'from-pink-50 to-rose-50',
                  borderColor: 'border-pink-200'
                },
                {
                  style: '水彩艺术',
                  prompt: '春天的田野，野花盛开，蝴蝶飞舞，水彩画风格，柔和色调，艺术感',
                  emoji: '🎨',
                  gradient: 'from-purple-50 to-violet-50',
                  borderColor: 'border-purple-200'
                },
                {
                  style: '赛博朋克',
                  prompt: '未来城市街道，霓虹灯招牌，雨夜反光，赛博朋克风格，高对比度，科幻感',
                  emoji: '🌃',
                  gradient: 'from-cyan-50 to-blue-50',
                  borderColor: 'border-cyan-200'
                }
              ].map((example, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ y: -8, scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <div 
                    className={`h-full cursor-pointer rounded-3xl border-2 ${example.borderColor} bg-gradient-to-br ${example.gradient} shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden p-8 group`}
                    onClick={() => {
                      form.setFieldsValue({ prompt: example.prompt })
                      message.success(`已应用"${example.style}"风格 ✨`)
                    }}
                    title={`点击应用：${example.prompt}`}
                  >
                    <div className="text-center">
                      <motion.div 
                        className="text-7xl mb-5"
                        whileHover={{ rotate: [0, -10, 10, -10, 0], scale: 1.1 }}
                        transition={{ duration: 0.5 }}
                      >
                        {example.emoji}
                      </motion.div>
                      <Title level={4} className="!mb-3 font-bold text-gray-800">{example.style}</Title>
                      <div className="inline-flex items-center gap-1.5 px-4 py-2 bg-white group-hover:bg-gradient-to-r group-hover:from-pink-50 group-hover:to-purple-50 rounded-full text-sm font-semibold text-gray-700 group-hover:text-pink-700 shadow-md mb-4 border border-gray-200 group-hover:border-pink-300 transition-all duration-300">
                        <span className="text-base">✨</span>
                        <span>点击应用</span>
                      </div>
                      <Paragraph 
                        ellipsis={{ rows: 3 }} 
                        className="text-sm text-gray-600 mb-0 leading-relaxed"
                      >
                        {example.prompt}
                      </Paragraph>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
            </div>
        </motion.div>
      </div>
    </div>
  )
}

export default ImageGenerationPage
